import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "xh-cookie-consent-v1";

const hasConsent = () => {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return false;
  }
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (hasConsent()) return;
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, []);

  const save = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, ts: new Date().toISOString() }),
      );
    } catch (e) {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[60] animate-fade-up"
      role="dialog"
      aria-labelledby="cookie-banner-title"
    >
      <div className="relative bg-bone-50 border border-bone-300 rounded-[1.5rem] shadow-lift p-6 md:p-7">
        <button
          onClick={() => save("dismissed")}
          aria-label="Schließen"
          data-testid="cookie-banner-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-bone-200 flex items-center justify-center text-ink-500 transition-colors"
        >
          <X strokeWidth={1.75} className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4">
          <span className="w-11 h-11 rounded-full bg-sage-300 text-sage-700 flex items-center justify-center shrink-0">
            <Cookie strokeWidth={1.75} className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div id="cookie-banner-title" className="font-display text-xl text-ink-900 leading-snug">
              Cookies &amp; Datenschutz
            </div>
            <p className="mt-2 text-ink-700 text-[14px] leading-relaxed">
              Diese Website nutzt ausschließlich technisch notwendige Cookies — keine Tracker, keine Werbung, keine Drittanbieter-Profile. Mehr dazu in der{" "}
              <Link to="/datenschutz" className="text-sage-600 link-underline">
                Datenschutz­erklärung
              </Link>
              .
            </p>

            {showDetails && (
              <div className="mt-4 space-y-2 text-[13px] text-ink-700 bg-bone-100 rounded-2xl p-4 border border-bone-200">
                <div className="flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 rounded-full bg-sage-500 shrink-0" />
                  <div>
                    <div className="text-ink-900 font-medium">Notwendige Cookies</div>
                    <div className="text-ink-500 text-[12.5px]">
                      Für grundlegende Funktionen. Aktiv, keine Einwilligung erforderlich.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 opacity-60">
                  <span className="mt-1 w-2 h-2 rounded-full bg-bone-300 shrink-0" />
                  <div>
                    <div className="text-ink-900 font-medium">Analyse & Marketing</div>
                    <div className="text-ink-500 text-[12.5px]">
                      Aktuell nicht im Einsatz.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                onClick={() => save("accepted")}
                data-testid="cookie-banner-accept"
                className="inline-flex items-center justify-center px-5 h-11 rounded-full bg-sage-500 hover:bg-sage-600 text-bone-50 text-[13px] tracking-wide transition-all duration-300 hover:-translate-y-0.5"
              >
                Alles klar
              </button>
              <button
                onClick={() => save("essential-only")}
                data-testid="cookie-banner-essential"
                className="inline-flex items-center justify-center px-5 h-11 rounded-full bg-bone-100 hover:bg-bone-200 border border-bone-300 text-ink-900 text-[13px] tracking-wide transition-colors"
              >
                Nur Notwendige
              </button>
              <button
                onClick={() => setShowDetails((s) => !s)}
                data-testid="cookie-banner-details"
                className="text-[12.5px] font-mono-fine tracking-[0.14em] uppercase text-ink-500 hover:text-ink-900 transition-colors"
              >
                {showDetails ? "Weniger" : "Details"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
